import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
// Use the dedicated htlife axios instance — it automatically attaches
// Authorization: Bearer <htlifeAccessToken> and handles token refresh on 401.
// Do NOT use the default axios here (it carries EMR tokens which htlife rejects).
import htlifeAxios from "../../utils/htlifeAxios";
import ZegoRoom from "../../components/Telemedicine/ZegoRoom";
import "./MyAppointments.scss";

// ── helpers ─────────────────────────────────────────────────────────────────

const STATUS = { LIVE: "live", UPCOMING: "upcoming", COMPLETED: "completed", CANCELLED: "cancelled" };
const TAB_ALL = "all";

const initials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "?";

const parseStartTimeIst = (startTimeIst = "") => {
  if (!startTimeIst) return { date: "", time: "" };
  if (startTimeIst.includes("|")) {
    const [date, time] = startTimeIst.split("|");
    return { date: date.trim(), time: time.trim() };
  }
  try {
    const d = new Date(startTimeIst);
    return {
      date: d.toISOString().split("T")[0],
      time: d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }),
    };
  } catch {
    return { date: startTimeIst, time: "" };
  }
};

const formatDateTime = (dateStr, timeStr) => {
  if (!dateStr) return "—";
  try {
    let d;
    if (timeStr && /am|pm/i.test(timeStr)) {
      d = new Date(`${dateStr} ${timeStr}`);
    } else {
      d = new Date(`${dateStr}T${timeStr || "00:00"}`);
    }
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = d.toDateString() === tomorrow.toDateString();

    const label = isToday ? "Today" : isTomorrow ? "Tomorrow" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    const time = timeStr ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";
    return time ? `${label} • ${time}` : label;
  } catch {
    return dateStr;
  }
};

const normalizeStatus = (raw = "") => {
  const s = raw.toLowerCase();
  if (s.includes("live") || s.includes("active") || s.includes("ongoing")) return STATUS.LIVE;
  if (s.includes("cancel")) return STATUS.CANCELLED;
  if (s.includes("complet") || s.includes("done") || s.includes("finish")) return STATUS.COMPLETED;
  return STATUS.UPCOMING;
};

// If the API still says "upcoming" but the slot is >30 min in the past, treat as completed.
const getEffectiveStatus = (appt) => {
  const apiStatus = normalizeStatus(appt.status);
  if (apiStatus !== STATUS.UPCOMING) return apiStatus;
  const { date: parsedDate, time: parsedTime } = parseStartTimeIst(appt.start_time_ist);
  const dateStr = appt.appointment_date || appt.date || parsedDate;
  const timeStr = appt.appointment_time || appt.time || parsedTime;
  try {
    if (!dateStr) return apiStatus;
    const apptTime = timeStr && /am|pm/i.test(timeStr)
      ? new Date(`${dateStr} ${timeStr}`)
      : new Date(`${dateStr}T${timeStr || "00:00"}`);
    return (new Date() - apptTime) / 60000 > 30 ? STATUS.COMPLETED : apiStatus;
  } catch { return apiStatus; }
};

// ── Sub-components ───────────────────────────────────────────────────────────

function PulseDot() {
  return <span className="pulse-dot" />;
}

function StatusBadge({ status }) {
  const map = {
    [STATUS.LIVE]: { label: "Live now", cls: "my-appts__badge--live", dot: true },
    [STATUS.UPCOMING]: { label: "Upcoming", cls: "my-appts__badge--upcoming", dot: false },
    [STATUS.COMPLETED]: { label: "Completed", cls: "my-appts__badge--completed", dot: false },
    [STATUS.CANCELLED]: { label: "Cancelled", cls: "my-appts__badge--cancelled", dot: false },
  };
  const { label, cls, dot } = map[status] || map[STATUS.UPCOMING];
  return (
    <span className={`my-appts__badge ${cls}`}>
      {dot && <PulseDot />}
      {label}
    </span>
  );
}

function MoreMenu({ items, onClose }) {
  const ref = useRef();
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div className="my-appts__menu" ref={ref}>
      {items.map((item) => (
        <button
          key={item.label}
          className={`my-appts__menu-item${item.danger ? " my-appts__menu-item--danger" : ""}`}
          onClick={() => { item.onClick(); onClose(); }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function AppointmentCard({ appt, onRebook, onViewPrescription, patientId, patientName, onJoinConsult, telemedicineEnabled }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const doctorName = appt.doctor_name || appt.doctorName || "Doctor";
  const specialty = appt.specialization || appt.specialty || appt.doctor_specialty || "";
  const reason = appt.reason || appt.chief_complaint || "";
  const { date: parsedDate, time: parsedTime } = parseStartTimeIst(appt.start_time_ist);
  const dateStr = appt.appointment_date || appt.date || parsedDate;
  const timeStr = appt.appointment_time || appt.time || parsedTime;
  const duration = appt.duration || appt.slot_duration || "";
  const rawMode = appt.appointment_mode || appt.consult_mode || appt.mode || "";
  const mode = rawMode === "ONLINE" ? "Online" : rawMode === "OFFLINE" ? "In-Clinic" : rawMode || "Video Consult";
  const fee = appt.fee || appt.consultation_fee || "";
  const joinUrl = appt.join_url || appt.video_url || "";
  const effectivePatientId = appt.patient_id || patientId || appt.patient || "";
  const zegoJoinUrl = effectivePatientId
    ? `/patient/tele-console-patient?room=${encodeURIComponent(`hlthtek-${effectivePatientId}`)}&name=${encodeURIComponent(patientName || patientId || "Patient")}`
    : "";
  const effectiveJoinUrl = joinUrl || zegoJoinUrl;
  const location = appt.location || "";
  const doctorPhoto = appt.doctor_photo || "";
  const waitTime = appt.expected_wait_time || 0;

  const status = getEffectiveStatus(appt);

  const isWithin60Minutes = (() => {
    try {
      if (!dateStr) return false;
      const apptTime = timeStr && /am|pm/i.test(timeStr)
        ? new Date(`${dateStr} ${timeStr}`)
        : new Date(`${dateStr}T${timeStr || "00:00"}`);
      const diff = (apptTime - new Date()) / 60000;
      return diff <= 60 && diff >= -30;
    } catch { return false; }
  })();

  const isJoinable = status === STATUS.LIVE || (status === STATUS.UPCOMING && isWithin60Minutes);

  const modeIcon =
    mode?.toLowerCase().includes("audio") ? (
      <PhoneIcon />
    ) : mode?.toLowerCase().includes("chat") ? (
      <ChatIcon />
    ) : (
      <VideoIcon />
    );

  const menuItems = [];
  if (status === STATUS.UPCOMING) {
    menuItems.push({ label: "Reschedule", onClick: () => toast.info("Reschedule feature coming soon.") });
    menuItems.push({ label: "Cancel appointment", danger: true, onClick: () => toast.info("Cancel feature coming soon.") });
  }

  return (
    <div className={`my-appts__card${status === STATUS.LIVE ? " my-appts__card--live" : ""}`}>
      <div className="my-appts__card-top">
        <div className="my-appts__card-avatar">
          {doctorPhoto ? (
            <img
              src={`data:image/jpeg;base64,${doctorPhoto}`}
              alt={doctorName}
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
            />
          ) : (
            initials(doctorName)
          )}
        </div>

        <div className="my-appts__card-info">
          <div className="my-appts__card-name-row">
            <span className="my-appts__card-name">{doctorName}</span>
            <StatusBadge status={status} />
          </div>
          {specialty && <div className="my-appts__card-specialty">{specialty}</div>}
          {location && <div className="my-appts__card-specialty" style={{ color: "#6b7a99" }}>{location}</div>}
          {reason && (
            <div className="my-appts__card-reason">
              Reason: <strong>{reason}</strong>
            </div>
          )}
        </div>

        {menuItems.length > 0 && (
          <div style={{ position: "relative", flexShrink: 0 }}>
            <button className="my-appts__card-more" onClick={() => setMenuOpen((v) => !v)} aria-label="More options">
              ⋮
            </button>
            {menuOpen && <MoreMenu items={menuItems} onClose={() => setMenuOpen(false)} />}
          </div>
        )}
      </div>

      <div className="my-appts__card-meta">
        {dateStr && (
          <span className="my-appts__card-meta-item">
            <CalendarIcon />
            {formatDateTime(dateStr, timeStr)}
          </span>
        )}
        {duration && (
          <span className="my-appts__card-meta-item">
            <ClockIcon />
            {duration} min
          </span>
        )}
        {waitTime > 0 && (
          <span className="my-appts__card-meta-item">
            <ClockIcon />
            ~{waitTime} min wait
          </span>
        )}
        <span className="my-appts__card-meta-item">
          {modeIcon}
          {mode}
        </span>
        {fee && <span className="my-appts__card-fee">₹{fee}</span>}

        <div className="my-appts__card-actions">
          {telemedicineEnabled && status === STATUS.LIVE && (
            <button
              className="my-appts__btn my-appts__btn--join"
              onClick={() => joinUrl ? window.open(joinUrl, "_blank") : toast.info("Joining session...")}
            >
              <VideoIcon /> Join now
            </button>
          )}

          {telemedicineEnabled && status === STATUS.UPCOMING && (
            <button
              className={`my-appts__btn my-appts__btn--join-consult${isJoinable ? " my-appts__btn--join-consult--active" : ""}`}
              disabled={!isJoinable}
              onClick={() => {
                if (!isJoinable) return;
                if (effectivePatientId) {
                  onJoinConsult?.({ roomID: `hlthtek-${effectivePatientId}`, userName: patientName || patientId || "Patient", doctorName });
                } else {
                  toast.info("Your doctor will share the meeting link soon.");
                }
              }}
            >
              <VideoIcon /> Join consult
            </button>
          )}

          {status === STATUS.COMPLETED && (
            <button className="my-appts__btn my-appts__btn--ghost" onClick={() => onViewPrescription && onViewPrescription(appt)}>
              View prescription
            </button>
          )}

          {status === STATUS.CANCELLED && (
            <button className="my-appts__btn my-appts__btn--rebook" onClick={() => onRebook && onRebook(appt)}>
              Rebook
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <>
      {[1, 2, 3].map((n) => (
        <div key={n} className="my-appts__loading-card">
          <div className="skel-row" style={{ width: "60%", height: 16 }} />
          <div className="skel-row" style={{ width: "40%", height: 12 }} />
          <div className="skel-row" style={{ width: "80%", height: 12, marginTop: 14 }} />
        </div>
      ))}
    </>
  );
}

// ── Inline SVG icons ─────────────────────────────────────────────────────────

const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const VideoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);
const PhoneIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const ChatIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const BackArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const BookIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="12" y1="14" x2="12" y2="18" />
    <line x1="10" y1="16" x2="14" y2="16" />
  </svg>
);
const InfoCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ── Main component ────────────────────────────────────────────────────────────

function MyAppointments({ htlifeAccessToken, patientUsername, patientPassword, patientId, patientName, orgCode, appMode, onBack, onBookNew }) {

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(TAB_ALL);
  const [activeCall, setActiveCall] = useState(null); // { roomID, userName }
  const patientUserIdRef = useRef(`patient-${patientId || Date.now()}`);
  const sharingRef = useRef(false);
  const telemedicineEnabled = sessionStorage.getItem("htlifeTelemedicineEnabled") === "true";

  // Sync the latest htlife access token into sessionStorage so the interceptor
  // in htlifeAxios.js always picks up the freshest token from props.
  useEffect(() => {
    if (htlifeAccessToken) {
      sessionStorage.setItem("htlifeAccessToken", htlifeAccessToken);
    }
  }, [htlifeAccessToken]);

  const fetchAppointments = useCallback(async () => {
    // Guard: no token means the patient is not logged into htlife yet.
    const token = htlifeAccessToken || sessionStorage.getItem("htlifeAccessToken") || "";
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // htlifeAxios automatically attaches Authorization + Org-Code + App-Mode
      // and will silently refresh the token on 401 before retrying.
      const response = await htlifeAxios.get("get_upcomming_appointments/");
      const raw = response?.data?.data || response?.data?.results || response?.data || [];
      setAppointments(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error("Failed to fetch appointments:", err);
      toast.error("Could not load appointments. Please try again.");
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [htlifeAccessToken]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // ── Derived counts ──
  const counts = appointments.reduce(
    (acc, a) => {
      const s = getEffectiveStatus(a);
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    { [STATUS.LIVE]: 0, [STATUS.UPCOMING]: 0, [STATUS.COMPLETED]: 0, [STATUS.CANCELLED]: 0 }
  );

  const hasLive = counts[STATUS.LIVE] > 0;

  // ── Filtering ──
  const filtered = appointments.filter((a) => {
    const status = getEffectiveStatus(a);
    const matchesTab = activeTab === TAB_ALL || status === activeTab;
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (a.doctor_name || a.doctorName || "").toLowerCase().includes(q) ||
      (a.specialty || a.doctor_specialty || "").toLowerCase().includes(q) ||
      (a.reason || a.chief_complaint || "").toLowerCase().includes(q);
    return matchesTab && matchesSearch;
  });

  const tabs = [
    { key: TAB_ALL, label: "All", count: appointments.length },
    { key: STATUS.UPCOMING, label: "Upcoming", count: counts[STATUS.UPCOMING] + counts[STATUS.LIVE] },
    { key: STATUS.COMPLETED, label: "Completed", count: counts[STATUS.COMPLETED] },
    { key: STATUS.CANCELLED, label: "Cancelled", count: counts[STATUS.CANCELLED] },
  ];

  const emptyMessages = {
    [TAB_ALL]: { icon: "📅", title: "No appointments yet", body: "Book a consultation to get started." },
    [STATUS.UPCOMING]: { icon: "🗓️", title: "No upcoming appointments", body: "You have no scheduled consultations." },
    [STATUS.COMPLETED]: { icon: "✅", title: "No completed appointments", body: "Your completed consultations will appear here." },
    [STATUS.CANCELLED]: { icon: "❌", title: "No cancelled appointments", body: "Cancelled consultations will appear here." },
  };
  const emptyState = emptyMessages[activeTab] || emptyMessages[TAB_ALL];


  if (activeCall) {
    const guestLink = `${window.location.origin}/join-call?room=${encodeURIComponent(activeCall.roomID)}`;
    const inviteText = `Hi! You are invited to join ${activeCall.userName}'s consultation with ${activeCall.doctorName}.\n\nClick the link below to join the video call:\n${guestLink}`;
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", backgroundColor: "#0e1117" }}>
        <div style={{ backgroundColor: "#0E3F66", color: "#fff", display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", flexShrink: 0, flexWrap: "wrap" }}>
          <button
            style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 14, fontWeight: 600 }}
            onClick={() => setActiveCall(null)}
          >
            ← Back
          </button>
          <span style={{ fontWeight: 700, fontSize: 16, flex: 1 }}>Telemedicine Consultation</span>
          <button
            onClick={async () => {
              if (sharingRef.current) return;
              sharingRef.current = true;
              try {
                await navigator.share({
                  title: "Telemedicine Consultation Invite",
                  text: inviteText,
                  url: guestLink,
                });
              } catch (err) {
                if (err.name !== "AbortError") {
                  navigator.clipboard.writeText(inviteText)
                    .then(() => toast.success("Invite copied to clipboard!"))
                    .catch(() => toast.error("Could not share or copy the invite."));
                }
              } finally {
                sharingRef.current = false;
              }
            }}
            style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
          >
            Share Invite
          </button>
        </div>
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <ZegoRoom
            roomID={activeCall.roomID}
            userID={patientUserIdRef.current}
            userName={activeCall.userName}
            height="100%"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="my-appts">
      {/* Header */}
      <div className="my-appts__header">
        <div className="my-appts__header-left">
          <button className="my-appts__back-btn" onClick={onBack} aria-label="Go back">
            <BackArrow />
          </button>
          <div className="my-appts__title">
            My Appointments
            <span>Manage and join your consultations</span>
          </div>
        </div>
        <button className="my-appts__book-btn" onClick={onBookNew}>
          <BookIcon /> Book new
        </button>
      </div>

      {/* Live banner */}
      {hasLive && (
        <div className="my-appts__live-banner">
          <span className="banner-icon">
            <InfoCircleIcon />
          </span>
          <div className="my-appts__live-banner-text">
            <strong>Your doctor is ready to see you now</strong>
            <p>You have {counts[STATUS.LIVE]} live consultation waiting. Join below.</p>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="my-appts__stats">
        {[
          { label: "Upcoming", key: STATUS.UPCOMING, extra: counts[STATUS.LIVE] },
          { label: "Live Now", key: STATUS.LIVE, live: true },
          { label: "Completed", key: STATUS.COMPLETED },
          { label: "Cancelled", key: STATUS.CANCELLED },
        ].map(({ label, key, live, extra = 0 }) => (
          <div
            key={key}
            className={`my-appts__stat-card${live ? " my-appts__stat-card--live" : ""}${activeTab === key ? " active" : ""}`}
            onClick={() => setActiveTab(key)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setActiveTab(key)}
          >
            <div className="my-appts__stat-card-label">{label}</div>
            <div className="my-appts__stat-card-count">{counts[key] + extra || counts[key]}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="my-appts__search-row">
        <div className="my-appts__search-wrap">
          <span className="search-icon"><SearchIcon /></span>
          <input
            type="text"
            placeholder="Search by doctor, specialty, or reason"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className="my-appts__filter-btn" aria-label="Filter" onClick={() => toast.info("Filters coming soon.")}>
          <FilterIcon />
        </button>
      </div>

      {/* Tabs */}
      <div className="my-appts__tabs">
        {tabs.map(({ key, label, count }) => (
          <button
            key={key}
            className={`my-appts__tab${activeTab === key ? " active" : ""}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
            {count > 0 && <span className="tab-count">{count}</span>}
          </button>
        ))}
      </div>

      {/* Cards */}
      <div className="my-appts__cards">
        {loading ? (
          <LoadingSkeleton />
        ) : filtered.length === 0 ? (
          <div className="my-appts__empty">
            <div className="my-appts__empty-icon">{emptyState.icon}</div>
            <h5>{emptyState.title}</h5>
            <p>{emptyState.body}</p>
            <button className="my-appts__btn my-appts__btn--rebook" onClick={onBookNew}>
              Book a consultation
            </button>
          </div>
        ) : (
          filtered.map((appt, idx) => (
            <AppointmentCard
              key={appt.id || appt.appointment_id || idx}
              appt={appt}
              onRebook={onBookNew}
              onViewPrescription={() => toast.info("Prescription viewer coming soon.")}
              patientId={patientId}
              patientName={patientName}
              onJoinConsult={setActiveCall}
              telemedicineEnabled={telemedicineEnabled}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default MyAppointments;
